import { Observable, catchError, of } from 'rxjs';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import { pieVersionError } from './version-front';

// Evita el warning "Blocked aria-hidden on an element because its descendant
// retained focus": SweetAlert2 marca <app-root> con aria-hidden al abrirse,
// por lo que cualquier botón con foco queda dentro de un ancestro oculto.
const blurActiveElement = (): void => {
  const active = document.activeElement as HTMLElement | null;
  if (active && typeof active.blur === 'function') {
    active.blur();
  }
};

const swalFire = ((...args: unknown[]) => {
  blurActiveElement();
  // Punto único: toda alerta de error que pase por los helpers muestra al pie la
  // versión del front (solo muta cuando icon === 'error'; el resto queda igual).
  const [config, ...rest] = args;
  if (config && typeof config === 'object') {
    pieVersionError(config as { icon?: unknown });
  }
  return (Swal.fire as (...a: unknown[]) => Promise<unknown>)(config, ...rest);
}) as typeof Swal.fire;

class SwalAlertTypes {
  private constructor() {}
  static readonly INFO = 'info';
  static readonly WARNING = 'warning';
  static readonly ERROR = 'error';
  static readonly SUCCESS = 'success';
  static readonly QUESTION = 'question';
  static readonly DEFAULT = 'default';
}

class SwalAlertTitles {
  private constructor() {}
  static readonly INFO = 'Información';
  static readonly WARNING = 'Atención';
  static readonly ERROR = 'Error';
  static readonly SUCCESS = 'Éxito';
  static readonly QUESTION = 'Atención';
  static readonly DEFAULT = 'Ups';
  static readonly STOP = 'Alto';
}

const showBasicAlert = (
  title: SwalAlertTitles | string,
  text: string,
  icon: SweetAlertIcon | any | undefined = undefined,
  onClose?: () => void,
  popupClass?: string,
) => {
  swalFire({
    title,
    html: text,
    icon,
    confirmButtonText: 'Cerrar',
    allowEscapeKey: false,
    customClass: {
      container: 'high-z-index-swal',
      popup: popupClass,
    },
    heightAuto: false,
    allowOutsideClick: false,
  }).then((result) => {
    if ((result.isConfirmed || result.isDismissed) && onClose) {
      onClose();
    }
  });
};

const showBasicAlertTimer = (
  title: string,
  text: string,
  icon: any,
  leyendaBoton = 'Cerrar',
  timer = 10,
) => {
  swalFire({
    title,
    text,
    icon,
    confirmButtonText: leyendaBoton,
    timer: timer,
    allowEscapeKey: false,
    customClass: {
      container: 'high-z-index-swal',
    },
    heightAuto: false,
    allowOutsideClick: false,
  });
};

const showBasicAlertCountdown = (
  title: string,
  text: string,
  icon: any,
  countdownSeconds: number = 10,
  onClose?: () => void,
) => {
  let timeLeft = countdownSeconds;

  const timerInterval = setInterval(() => {
    timeLeft--;

    // Actualizar el texto del botón con la cuenta regresiva
    const confirmButton = document.querySelector(
      '.swal2-confirm',
    ) as HTMLButtonElement;
    if (confirmButton) {
      confirmButton.textContent = `Cerrar (${timeLeft}s)`;
      confirmButton.disabled = timeLeft > 0;
    }

    if (timeLeft <= 0) {
      clearInterval(timerInterval);
      if (confirmButton) {
        confirmButton.textContent = 'Cerrar';
        confirmButton.disabled = false;
      }
    }
  }, 1000);

  swalFire({
    title,
    html: text,
    icon,
    confirmButtonText: `Cerrar (${countdownSeconds}s)`,
    allowEscapeKey: false,
    allowOutsideClick: false,
    showConfirmButton: true,
    customClass: {
      container: 'high-z-index-swal',
    },
    heightAuto: false,
    didOpen: () => {
      const confirmButton = document.querySelector(
        '.swal2-confirm',
      ) as HTMLButtonElement;
      if (confirmButton) {
        confirmButton.disabled = true;
      }
    },
  }).then((result) => {
    clearInterval(timerInterval);
    if ((result.isConfirmed || result.isDismissed) && onClose) {
      onClose();
    }
  });
};

const showArrayAlert = (
  title: string,
  text: string[],
  icon: any,
  popupClass?: string,
) => {
  swalFire({
    title,
    html: text
      .map((item: any) => {
        return item.msg;
      })
      .join('</br>'),
    icon,
    allowEscapeKey: false,
    customClass: {
      container: 'high-z-index-swal',
      popup: popupClass,
    },
    heightAuto: false,
    allowOutsideClick: false,
  });
};
/**
 * @param title Titulo de la alerta
 * @param text  Cuerpo de la alerta
 * @param icon Tipo de alerta (success,error,info,question,warning)
 * @param width Ancho de la alerta
 * @param confirmButtonText Texto del boton de confirmar
 * @param cancelButtonText Texto del boton de cancelar
 * @returns Retorna una promesa con la opción seleccionada
 */
const showConfirmAlert = (
  title: string,
  text: string,
  icon: any = 'info',
  width?: string,
  confirmButtonText?: string,
  cancelButtonText?: string,
  escapeKey: boolean = false,
  popupClass?: string,
) => {
  return swalFire({
    title,
    html: text,
    icon,
    showCancelButton: true,
    confirmButtonColor: '#3f51b5',
    cancelButtonColor: 'gray',
    confirmButtonText: confirmButtonText || 'Confirmar!',
    cancelButtonText: cancelButtonText || 'Cancelar',
    reverseButtons: true,
    allowEscapeKey: escapeKey,
    allowOutsideClick: false,
    width,
    customClass: {
      container: 'high-z-index-swal',
      popup: popupClass,
    },
    heightAuto: false,
  });
};

/**
 * Como `showConfirmAlert`, pero con una cuenta regresiva visible en el botón
 * de confirmar — al llegar a 0 se autoconfirma (`Swal.clickConfirm()`), como
 * si el usuario hubiera aceptado. Pensado para preguntas de "¿pasó X?" donde
 * detener siempre el flujo del usuario a esperar una respuesta manual es más
 * molesto que dar por hecho el caso más común (ej. "¿se imprimió
 * correctamente?" — lo usual es que sí).
 * @param segundos Segundos antes de autoconfirmar (default 10).
 * @returns Misma promesa que `showConfirmAlert` — `isConfirmed` es `true`
 * tanto si el usuario confirma a mano como si expira el tiempo.
 */
const showConfirmAlertConTemporizador = (
  title: string,
  text: string,
  icon: any = 'info',
  segundos: number = 10,
  confirmButtonText?: string,
  cancelButtonText?: string,
  popupClass?: string,
) => {
  const etiquetaBase = confirmButtonText || 'Confirmar!';
  let restante = segundos;

  const intervalo = setInterval(() => {
    restante--;
    const botonConfirmar = document.querySelector(
      '.swal2-confirm',
    ) as HTMLButtonElement | null;
    if (botonConfirmar) {
      botonConfirmar.textContent = `${etiquetaBase} (${restante}s)`;
    }
    if (restante <= 0) {
      clearInterval(intervalo);
      Swal.clickConfirm();
    }
  }, 1000);

  return swalFire({
    title,
    html: text,
    icon,
    showCancelButton: true,
    confirmButtonColor: popupClass ? undefined : '#3f51b5',
    cancelButtonColor: popupClass ? undefined : 'gray',
    confirmButtonText: `${etiquetaBase} (${segundos}s)`,
    cancelButtonText: cancelButtonText || 'Cancelar',
    reverseButtons: true,
    allowEscapeKey: false,
    allowOutsideClick: false,
    heightAuto: false,
    customClass: {
      container: 'high-z-index-swal',
      popup: popupClass,
    },
  }).then((result) => {
    clearInterval(intervalo);
    return result;
  });
};

/**
 * @param title Titulo de la alerta
 * @param text  Cuerpo de la alerta
 * @param icon Tipo de alerta (success,error,info,question,warning)
 * @param width Ancho de la alerta
 * @param onClose Función a ejecutar cuando se cierra la alerta
 * @param permitirCerrar Permite cerrar la alerta
 * @returns Retorna una promesa con la opción seleccionada
 */
const showAlertHtml = (
  title: string,
  text: string,
  icon: any = 'info',
  width?: string,
  onClose?: () => void,
  permitirCerrar: boolean = true,
  showCancelButton: boolean = false,
  confirmButtonText: string = 'Cerrar',
  cancelButtonText: string = 'Cancelar',
) => {
  return swalFire({
    title,
    html: text,
    icon,
    showCancelButton: showCancelButton, // Si se muestra el boton de cancelar
    cancelButtonText: cancelButtonText,
    confirmButtonText: confirmButtonText,
    reverseButtons: showCancelButton, // Si se muestra el boton de cancelar, se invierte el orden de los botones
    allowEscapeKey: permitirCerrar,
    allowOutsideClick: permitirCerrar,
    width,
    customClass: {
      container: 'high-z-index-swal',
      icon: 'custom-icon',
      title: 'custom-title',
      popup: 'custom-popup',
      htmlContainer: 'mi-html-personalizado',
    },
    heightAuto: false,
  }).then((result) => {
    if (result.isConfirmed) {
      if (onClose) onClose();
      return true;
    } else {
      if (onClose) onClose();
      return false;
    }
  });
};

/**
 * @param title Titulo de la alerta
 * @param text  Cuerpo de la alerta
 * @param icon Tipo de alerta (success,error,info,question,warning)
 * @param width Ancho de la alerta
 * @param onClose Función a ejecutar cuando se cierra la alerta
 * @param permitirCerrar Permite cerrar la alerta
 * @param showCancelButton Indica si se muestra el boton de cancelar
 * @param confirmButtonText Texto del boton de confirmar
 * @param cancelButtonText Texto del boton de cancelar
 * @returns Retorna una promesa con la opción seleccionada
 */
const showConfirmAlertHtml = (
  title: string,
  text: string,
  icon: any = 'info',
  width?: string,
  onClose?: () => void,
  permitirCerrar: boolean = true,
  showCancelButton: boolean = false,
  confirmButtonText: string = 'Cerrar',
  cancelButtonText: string = 'Cancelar',
  popupClass?: string,
) => {
  return swalFire({
    title,
    html: text,
    icon,
    showCancelButton: showCancelButton, // Si se muestra el boton de cancelar
    cancelButtonText: cancelButtonText,
    confirmButtonText: confirmButtonText,
    reverseButtons: showCancelButton, // Si se muestra el boton de cancelar, se invierte el orden de los botones
    allowEscapeKey: permitirCerrar,
    allowOutsideClick: permitirCerrar,
    width,
    customClass: {
      container: 'high-z-index-swal',
      icon: 'custom-icon',
      title: 'custom-title',
      popup: popupClass ? `custom-popup ${popupClass}` : 'custom-popup',
      htmlContainer: 'mi-html-personalizado',
    },
    heightAuto: false,
  }).then((result) => {
    if (result.isConfirmed) {
      if (onClose) onClose();
      return true;
    } else {
      if (onClose) onClose();
      return false;
    }
  });
};

/**
 * @param title Titulo de la alerta
 * @param text  Cuerpo de la alerta
 * @param icon Tipo de alerta (success,error,info,question,warning)
 * @returns Retorna una promesa con la opción seleccionada
 */
const showConfirmAlertPost = (
  title: string,
  text: string,
  icon: any = 'info',
  servicio: Observable<any>,
) => {
  return swalFire({
    title,
    html: text,
    icon,
    showCancelButton: true,
    confirmButtonColor: '#3f51b5',
    cancelButtonColor: 'gray',
    confirmButtonText: 'Confirmar!',
    cancelButtonText: 'Cerrar',
    reverseButtons: true,
    showLoaderOnConfirm: true,
    allowEscapeKey: false,
    preConfirm: () => {
      return servicio; //this.servicioReportes.cambiarEstadoReporte(reporte,3)
    },
    allowOutsideClick: () => !Swal.isLoading(),
  });
};

//
/**
 * @param title Titulo de la alerta
 * @param text  Cuerpo de la alerta
 * @param icon Tipo de alerta (success,error,info,question,warning)
 * @returns Retorna una promesa con la opción seleccionada
 */
const showConfirmAlertPostDeny = (
  title: string,
  text: string,
  icon: any = 'info',
  servicio: Observable<any>,
  servicioDeny: Observable<any>,
) => {
  return swalFire({
    title,
    html: text,
    icon,
    showCancelButton: true,
    showDenyButton: true,
    denyButtonText: `Rechazar!`,
    confirmButtonColor: '#3f51b5',
    cancelButtonColor: 'gray',
    confirmButtonText: 'Confirmar!',
    cancelButtonText: 'Cerrar',
    reverseButtons: true,
    showLoaderOnConfirm: true,
    allowEscapeKey: false,
    preConfirm: () => {
      return servicio; //this.servicioReportes.cambiarEstadoReporte(reporte,3)
    },
    preDeny: () => {
      return servicioDeny;
    },
    allowOutsideClick: () => !Swal.isLoading(),
  });
};

/**
 * @param title Titulo de la alerta
 * @param text  Cuerpo de la alerta
 * @param icon Tipo de alerta (success,error,info,question,warning)
 * @param servicio Servicio a ejecutar
 * @param inputRequerido Indica si el input es requerido
 * @returns Retorna una promesa con la opción seleccionada
 */
const showConfirmAlertPostTextarea = (
  title: string,
  text: string,
  icon: any = 'info',
  servicio: { servicio: any; funcion: string; parametros: any },
  inputRequerido: boolean = false,
  popupClass?: string,
) => {
  return swalFire({
    title,
    html: text,
    icon,
    input: 'textarea',
    inputPlaceholder: 'Comentarios',

    showCancelButton: true,
    confirmButtonColor: '#3f51b5',
    cancelButtonColor: 'gray',
    confirmButtonText: 'Confirmar!',
    cancelButtonText: 'Cerrar',
    reverseButtons: true,
    showLoaderOnConfirm: true,
    allowEscapeKey: false,
    customClass: {
      container: 'high-z-index-swal',
      popup: popupClass,
    },
    heightAuto: false,
    preConfirm: (comentario) => {
      if (inputRequerido && !comentario) {
        Swal.showValidationMessage('Este campo es requerido');
        // Aplicar fondo blanco al mensaje de validación
        setTimeout(() => {
          const validationMessage = document.querySelector(
            '.swal2-validation-message',
          ) as HTMLElement;
          if (validationMessage) {
            validationMessage.style.backgroundColor = '#fdfbff';
          }
        }, 0);
        return false;
      }
      servicio.parametros.comentario = comentario;
      const observable = servicio.servicio[servicio.funcion](
        servicio.parametros,
      );
      // Manejar errores del Observable para evitar que Swal se quede en carga
      return observable.pipe(
        catchError((error) => {
          // Retornar un objeto con el error para que Swal pueda manejarlo
          return of({
            error: true,
            msg: error?.error?.msg || error?.message || 'Error en la petición',
            payload: null,
          });
        }),
      );
    },
    allowOutsideClick: () => !Swal.isLoading(),
  });
};

/**
 * @param title Titulo de la alerta
 * @param text  Cuerpo de la alerta
 * @param icon Tipo de alerta (success,error,info,question,warning)
 * @returns Retorna una promesa con la opción seleccionada
 */
const showConfirmAlertPostDenyInput = async (
  title: string,
  text: string,
  icon: any = 'info',
  servicio: { servicio: any; funcion: string; parametros: any },
  servicioDeny: { servicio: any; funcion: string; parametros: any },
) => {
  return swalFire({
    title,
    html: text,
    icon,
    showCancelButton: true,
    showDenyButton: true,

    input: 'text',
    inputPlaceholder: 'Comentarios',
    returnInputValueOnDeny: true,
    denyButtonText: `Rechazar!`,
    confirmButtonColor: '#3f51b5',
    cancelButtonColor: 'gray',
    confirmButtonText: 'Confirmar!',
    cancelButtonText: 'Cerrar',
    reverseButtons: true,
    showLoaderOnConfirm: true,
    allowEscapeKey: false,

    preConfirm: (comentario) => {
      servicio.parametros.comentario = comentario;
      return servicio.servicio[servicio.funcion](servicio.parametros);
    },
    preDeny: (comentario) => {
      servicioDeny.parametros.comentario = comentario;
      return servicioDeny.servicio[servicioDeny.funcion](
        servicioDeny.parametros,
      );
    },
    allowOutsideClick: () => !Swal.isLoading(),
  });
};

/**
 * @param title Titulo de la alerta
 * @param text  Cuerpo de la alerta
 * @param icon Tipo de alerta (success,error,info,question,warning)
 * @returns Retorna una promesa con la opción seleccionada
 */
const showConfirmAlertPostHtmlFecha = (
  title: string,
  text: string,
  icon: any = 'info',
  servicio: { servicio: any; funcion: string; parametros: any },
  inputRequerido: boolean = false,
) => {
  const inputValue = undefined;
  return swalFire({
    title,
    // html: text,
    icon,
    html: `<input class="swal2-input" id="fechaConsulta" type="date" >`,
    inputPlaceholder: 'Fecha',
    showCancelButton: true,
    confirmButtonColor: '#3f51b5',
    cancelButtonColor: 'gray',
    confirmButtonText: 'Confirmar!',
    cancelButtonText: 'Cerrar',
    reverseButtons: true,
    showLoaderOnConfirm: true,
    allowEscapeKey: false,
    inputValue,
    preConfirm: (result) => {
      const input: any =
        Swal.getHtmlContainer()?.querySelector('#fechaConsulta');
      const fecha = input?.value;
      if (!result || fecha === '') {
        return false;
      }
      servicio.parametros.fecha = fecha;
      return servicio.servicio[servicio.funcion](servicio.parametros);
    },
    allowOutsideClick: () => !Swal.isLoading(),
  });
};

/**
 * @param title Titulo de la alerta
 * @param text  Cuerpo de la alerta
 * @param icon Tipo de alerta (success,error,info,question,warning)
 * @returns Retorna una promesa con la opción seleccionada
 */
const showConfirmAlertPostSelect = (
  title: string,
  text: string,
  icon: any = 'info',
  servicio: { servicio: any; funcion: string; parametros: any },
  inputRequerido: boolean = false,
  opciones: any,
  placeholder: string = '',
) => {
  return swalFire({
    title,
    html: text,
    icon,
    input: 'select',
    inputPlaceholder: placeholder,
    inputOptions: opciones,
    showCancelButton: true,
    confirmButtonColor: '#3f51b5',
    cancelButtonColor: 'gray',
    confirmButtonText: 'Confirmar!',
    cancelButtonText: 'Cerrar',
    reverseButtons: true,
    showLoaderOnConfirm: true,
    allowEscapeKey: false,
    preConfirm: (comentario) => {
      if (inputRequerido && !comentario) {
        return false;
      }
      const datos = comentario.split('-');
      servicio.parametros.folio = datos[0];
      servicio.parametros.empresa = datos[1];
      return servicio.servicio[servicio.funcion](servicio.parametros);
    },
    allowOutsideClick: () => !Swal.isLoading(),
  });
};

/**
 * @param title Titulo de la alerta
 * @param text  Cuerpo de la alerta
 * @param icon Tipo de alerta (success,error,info,question,warning)
 * @returns Retorna una promesa con la opción seleccionada
 */
const showConfirmAlertSelectSimple = (
  title: string,
  text: string,
  icon: any = 'info',
  inputRequerido: boolean = false,
  opciones: any,
  placeholder: string = '',
) => {
  return swalFire({
    title,
    html: text,
    icon,
    input: 'select',
    inputPlaceholder: placeholder,
    inputOptions: opciones,
    showCancelButton: true,
    confirmButtonColor: '#3f51b5',
    cancelButtonColor: 'gray',
    confirmButtonText: 'Confirmar!',
    cancelButtonText: 'Cerrar',
    reverseButtons: true,
    showLoaderOnConfirm: true,
    allowEscapeKey: false,
    preConfirm: (response) => {
      if (inputRequerido && !response) {
        return false;
      }
      return response;
    },
    allowOutsideClick: () => !Swal.isLoading(),
  });
};

/**
 * @param title Titulo de la alerta
 * @param text  Cuerpo de la alerta
 * @param icon Tipo de alerta (success,error,info,question,warning)
 * @param inputRequerido Indica si el input es requerido
 * @param placeholder Texto del placeholder
 * @returns Retorna una promesa con el texto ingresado si se confirma
 */
const showConfirmAlertTextarea = (
  title: string,
  text: string,
  icon: any = 'info',
  inputRequerido: boolean = false,
  placeholder: string = 'Comentarios',
  popupClass?: string,
  confirmButtonText: string = 'Confirmar!',
  cancelButtonText: string = 'Cerrar',
) => {
  return swalFire({
    title,
    html: text,
    icon,
    input: 'textarea',
    inputPlaceholder: placeholder,
    showCancelButton: true,
    confirmButtonColor: '#3f51b5',
    cancelButtonColor: 'gray',
    confirmButtonText,
    cancelButtonText,
    reverseButtons: true,
    allowEscapeKey: false,
    customClass: {
      container: 'high-z-index-swal',
      popup: popupClass,
    },
    heightAuto: false,
    allowOutsideClick: false,
    preConfirm: (comentario) => {
      if (inputRequerido && !comentario) {
        Swal.showValidationMessage('Este campo es requerido');
        // Aplicar fondo blanco al mensaje de validación
        setTimeout(() => {
          const validationMessage = document.querySelector(
            '.swal2-validation-message',
          ) as HTMLElement;
          if (validationMessage) {
            validationMessage.style.backgroundColor = '#fdfbff';
          }
        }, 0);
        return false;
      }
      return comentario;
    },
  });
};

const showSeleccionImpresion = (totalCajas: number, filtradas: number) => {
  return Swal.fire({
    title: 'Imprimir etiquetas',
    html: `Se detectó un filtro activo en la tabla.<br>¿Qué cajas deseas imprimir?`,
    icon: 'question',
    input: 'radio',
    inputOptions: {
      todas: `Imprimir todas las cajas (${totalCajas})`,
      filtradas: `Imprimir solo las filtradas (${filtradas})`,
    },
    inputValidator: (value) => (!value ? 'Selecciona una opción' : null),
    showCancelButton: true,
    confirmButtonColor: '#3f51b5',
    cancelButtonColor: 'gray',
    confirmButtonText: 'Imprimir',
    cancelButtonText: 'Cancelar',
    reverseButtons: true,
    allowEscapeKey: false,
    allowOutsideClick: false,
    customClass: { container: 'high-z-index-swal' },
    heightAuto: false,
  });
};

const showToastAlert = (
  text: string,
  icon: SweetAlertIcon = 'success',
  title: string = '',
  timer: number = 3000,
  tema: boolean | string = false,
) => {
  blurActiveElement();
  // `tema` acepta el boolean histórico (tema WMS, clase fija 'swal-wms-toast')
  // o directamente el nombre de la clase (otros temas, ej. 'swal-maja-toast').
  const popupClass = typeof tema === 'string' ? tema : tema ? 'swal-wms-toast' : undefined;
  // Nombre que usa team/wms en `showCloseButton`: con cualquier tema el toast lleva cierre.
  const temaWms = !!tema;
  const toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    showCloseButton: temaWms,
    timer: timer,
    timerProgressBar: true,
    customClass: popupClass ? { popup: popupClass } : undefined,
    didOpen: (toast) => {
      toast.onmouseenter = Swal.stopTimer;
      toast.onmouseleave = Swal.resumeTimer;
    },
  });
  toast.fire({
    icon: icon,
    title: `<strong>${title}</strong>`,
    text: text,
  });
};

export {
  showBasicAlert,
  showArrayAlert,
  showConfirmAlert,
  showConfirmAlertConTemporizador,
  showAlertHtml,
  showConfirmAlertHtml,
  showConfirmAlertPost,
  showConfirmAlertPostDeny,
  showConfirmAlertPostTextarea,
  showConfirmAlertTextarea,
  showConfirmAlertPostDenyInput,
  showConfirmAlertPostHtmlFecha,
  showConfirmAlertPostSelect,
  showConfirmAlertSelectSimple,
  showBasicAlertTimer,
  showBasicAlertCountdown,
  showToastAlert,
  showSeleccionImpresion,
  SwalAlertTypes,
  SwalAlertTitles,
};
